import { useState } from 'react';
import { Button } from '@ordr-as/umami-react/button';
import { PhoneInput, TextInput } from '@ordr-as/umami-react/form';
import { Switch } from '@ordr-as/umami-react/switch';
import useIntl from '../../../../../hooks/useIntl';
import { useCreateDigitalBill, useQueryUnsettledDigitalBills } from '../../../../../hooks/useDigitalBillApi';
import { useLocation } from '../../../../../context/LocationContext';
import Spinner from '../../../../../components/Spinner';
import { FormattedMessage } from 'react-intl';
import ExistingTableWarning from './ExistingTableWarning';
import BackButton from '../../../../../components/BackButton';
import InvoiceInfoContainer from '../../../../../components/InvoiceInfoContainer';
import classNames from 'clsx';
import { useParams } from 'react-router-dom';
import { useScreenSize } from '../../../../../context/ScreenSizeContext';
import CloseButton from '../../../../../components/CloseButton';
import { useDigitalBillActions } from '../../../../../context/DigitalBillStore';
import { useOpenBillPaymentMethods } from '../../../../../context/PaymentMethodStore';
import { isEmptyPhoneNumber, isValidPhoneNumber } from '../../../../../utils/phoneNumber';
import { Userpilot } from 'userpilot';

const SpinnerIcon = () => <Spinner className="self-center ml-1 w-5" />;

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

const CreateDigitalBillDialog = (props: Props) => {
  const { onClose, onCreated } = props;
  const intl = useIntl();
  const { isPhone } = useScreenSize();
  const { location } = useLocation();
  const { setActiveDigitalBill } = useDigitalBillActions();
  const { primaryReference } = useParams<{ primaryReference?: string }>();
  const predefinedTableNumber = primaryReference && Number.parseInt(primaryReference) ? primaryReference : undefined;
  const [tableNumber, setTableNumber] = useState<string>(predefinedTableNumber || '');
  const [tableName, setTableName] = useState<string>();
  const [numberOfGuests, setNumberOfGuests] = useState<string>();
  const [maxBillAmount, setMaxBillAmount] = useState<string>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const tableNumberWithoutLeading0s = tableNumber.replaceAll(/^0+/g, '');
  const { mutate, isPending } = useCreateDigitalBill(location.locationId);
  const [confirmUseExistingBill, setConfirmUseExistingBill] = useState<boolean>(false);

  const openBillPaymentMethods = useOpenBillPaymentMethods();
  const isInvoicePaymentMethodEnabled = openBillPaymentMethods.some(
    (paymentMethod) => paymentMethod.provider === 'UNINTEGRATED' && paymentMethod.paymentMethod === 'INVOICE'
  );
  const [isInvoiceCustomer, setIsInvoiceCustomer] = useState<boolean>(false);
  const [invoiceFieldValues, setInvoiceFieldValues] = useState<{
    customerRegistryId?: string;
    customerName?: string;
    referencePerson?: string;
    comment?: string;
  }>({});

  const tableNumberIsOnly0s = /^0*$/.test(tableNumber);
  const isInvalidTableNumber = !tableNumber || tableNumberIsOnly0s;

  const sanitizedPhoneNumber = phoneNumber.replaceAll(' ', '');
  const isInvalidPhoneNumber = !isEmptyPhoneNumber(phoneNumber) && !isValidPhoneNumber(phoneNumber);

  const { data: unsettledBills, isSuccess } = useQueryUnsettledDigitalBills(location.locationId);

  if (!isSuccess) {
    return null;
  }

  const existingBill = unsettledBills.find(
    (bill) => bill.primaryReference === tableNumber || bill.primaryReference === tableNumberWithoutLeading0s
  );
  const tableExists = Boolean(existingBill);

  const onInvoiceToggleChange = (isSelected: boolean) => {
    setInvoiceFieldValues({});
    setIsInvoiceCustomer(isSelected);
  };

  const handleCreateDigitalBill = () => {
    if (tableNumber && !isPending) {
      const sanitizedTableName = tableName?.trim().length ? tableName.trim() : undefined;
      const sanitizedNumberOfGuests =
        numberOfGuests && Number.parseInt(numberOfGuests) >= 1 ? Number.parseInt(numberOfGuests) : undefined;
      const sanitizedMaxBillAmount =
        maxBillAmount && Number.parseFloat(maxBillAmount) > 0 ? Number.parseFloat(maxBillAmount) : undefined;
      const guestInformationDto = isValidPhoneNumber(sanitizedPhoneNumber)
        ? { phone: sanitizedPhoneNumber }
        : undefined;

      const invoiceData = {
        comment: invoiceFieldValues.comment,
        customerName: invoiceFieldValues.customerName,
        customerRegistryId: invoiceFieldValues.customerRegistryId,
        referencePerson: invoiceFieldValues.referencePerson,
      };

      mutate(
        {
          data: {
            deliveryPoint: { primaryReference: tableNumberWithoutLeading0s },
            guestInformationDto,
            invoiceData: isInvoiceCustomer ? invoiceData : undefined,
            maxBillAmount: sanitizedMaxBillAmount,
            numberOfGuests: sanitizedNumberOfGuests,
            paymentRule: 'PAY_LATER',
            tableName: sanitizedTableName,
          },
          locationId: location.locationId,
        },
        {
          onSettled: () => {
            onCreated();
          },
          onSuccess: (data) => {
            if (sanitizedMaxBillAmount) {
              Userpilot.track('epos_openbills_bill_limit-set');
            }

            setActiveDigitalBill(data);
          },
        }
      );
    }
  };

  const handleAddToExistingBill = () => {
    const selectedDigitalBill = unsettledBills.find((bill) => bill.primaryReference === tableNumber)!;

    setActiveDigitalBill(selectedDigitalBill);

    onCreated();
  };

  const handleAction = () => {
    if (tableExists) {
      return handleAddToExistingBill();
    }

    handleCreateDigitalBill();
  };

  const shouldShowNoLeading0sHelpText = !tableExists && !tableNumberWithoutLeading0s && tableNumber.length > 0;

  const getButtonLabel = () => {
    if (tableExists) {
      return 'Select table';
    }

    return 'Open bill';
  };

  const invoiceSwitchClassName = classNames('flex justify-between items-center pb-3', {
    'border-b border-grey-lightest': isInvoiceCustomer,
  });

  const navigationTitle = predefinedTableNumber ? `Table ${predefinedTableNumber}` : 'Create table';

  return (
    <>
      {isPhone ? (
        <BackButton title={navigationTitle} navigateTo={onClose} />
      ) : (
        <CloseButton title={navigationTitle} navigateTo={onClose} />
      )}
      <article className="flex flex-col h-full justify-between">
        <section className="flex p-4 flex-col gap-5">
          <div className="flex gap-4">
            {!predefinedTableNumber && (
              <TextInput
                autoFocus
                value={tableNumber}
                onChange={setTableNumber}
                data-cy="table-number"
                label={intl.formatMessage({ id: `checkout.deliveryPoint.primary.TABLE` })}
                isDisabled={isPending}
                type="number"
                required
                fullWidth
                className="flex-[3]"
                IconElement={isPending ? SpinnerIcon : undefined}
              />
            )}
            <TextInput
              value={numberOfGuests}
              onChange={setNumberOfGuests}
              isDisabled={tableExists || isPending}
              label="Number of guests"
              data-cy="number-of-guests"
              type="number"
              fullWidth
              className="flex-[2]"
            />
          </div>

          {shouldShowNoLeading0sHelpText && (
            <p className="text-orange-darkest text-xs">
              <FormattedMessage id="digitalBill.create.leadingZerosDisallowed" />
            </p>
          )}
          {existingBill && (
            <ExistingTableWarning
              existingBill={existingBill}
              setConfirmUseExistingBill={setConfirmUseExistingBill}
              confirmUseExistingBill={confirmUseExistingBill}
            />
          )}

          <div className="flex gap-4">
            <TextInput
              value={tableName}
              onChange={setTableName}
              isDisabled={tableExists || isPending}
              label="Table name"
              data-cy="table-name"
              type="text"
              fullWidth
              className="flex-[3]"
            />
            <TextInput
              value={maxBillAmount}
              onChange={setMaxBillAmount}
              isDisabled={tableExists || isPending}
              label={intl.formatMessage({ id: 'digitalBill.create.billLimit.label' })}
              data-cy="max-bill-amount"
              type="number"
              fullWidth
              className="flex-[2]"
            />
          </div>
          <div>
            <PhoneInput
              label={intl.formatMessage({ id: 'digitalBill.phoneNumber.label' })}
              fullWidth
              isDisabled={tableExists || isPending}
              sendPhoneNumberToParent={setPhoneNumber}
              validationState={isInvalidPhoneNumber ? 'invalid' : undefined}
              invalidHelpText={intl.formatMessage({ id: 'digitalBill.phoneNumber.invalid' })}
            />
          </div>
          {isInvoicePaymentMethodEnabled && (
            <>
              <div className={invoiceSwitchClassName}>
                <span>Invoice customer</span>
                <Switch
                  aria-label="Toggle for invoice customer"
                  className="m-0"
                  isSelected={isInvoiceCustomer}
                  onChange={onInvoiceToggleChange}
                  isDisabled={tableExists || isPending}
                />
              </div>
              {isInvoiceCustomer && !tableExists && (
                <div>
                  <InvoiceInfoContainer setInvoiceFieldValues={setInvoiceFieldValues} />
                </div>
              )}
            </>
          )}
        </section>
        <div className="flex sticky bottom-0 bg-brand-white p-4">
          <Button
            kind="primary"
            data-cy="create-bill"
            fullWidth
            isDisabled={
              isInvalidTableNumber ||
              (tableExists && !confirmUseExistingBill) ||
              (!tableExists && isInvalidPhoneNumber) ||
              (isInvoiceCustomer && !invoiceFieldValues.customerRegistryId && !invoiceFieldValues.customerName)
            }
            onClick={handleAction}
          >
            {getButtonLabel()}
          </Button>
        </div>
      </article>
    </>
  );
};

export default CreateDigitalBillDialog;
